import { atom } from 'jotai';
import type { AnnotationType, BoundingBoxT } from '../../labelling/types/annotations.type';
export const keyframeStepperTrackingEnabledAtom = atom(false);
export type MlTrackingKeyframeT = {
  frameNo: number;
  boundingBox: BoundingBoxT;
};

/**
 * A single background ML-tracking job for one object (annotation). Each job
 * carries all of that object's adjusted keyframe boxes plus the frame range to
 * track; the runner drains the queue serially (one object at a time) and issues
 * one batch tracking call per job.
 *
 * The wire fields (`keyFrames` / `startFrame` / `endFrame`) map 1:1 to the batch
 * API input; `id` / `annotationId` / `displayName` / `annotationType` are
 * client-only (queue identity, toast text, object type) and are not sent.
 */
export type MlTrackingJobT = {
  id: string;
  annotationId: string;
  displayName: string;
  annotationType: AnnotationType;
  keyFrames: MlTrackingKeyframeT[];
  startFrame: number;
  endFrame: number;
};
export type MlTrackingStatusT = 'in-progress' | 'completed' | 'error';
export type MlTrackingProgressEntryT = {
  displayName: string;
  status: MlTrackingStatusT;
};
export const mlTrackingJobsAtom = atom<MlTrackingJobT[]>([]);
export const mlTrackingProgressAtom = atom<Map<string, MlTrackingProgressEntryT>>(new Map<string, MlTrackingProgressEntryT>());

/**
 * True while any background tracking job is queued or being processed. The runner keeps a job in
 * `mlTrackingJobsAtom` for its whole lifecycle and pops it only in its `finally` (even on failure),
 * so a non-empty queue is the exact "tracking is running" window. Used to block task submit and
 * suppress auto-pause; inert when the feature flag is off (nothing enqueues ⇒ queue stays empty).
 */
export const isTrackingActiveAtom = atom(get => get(mlTrackingJobsAtom).length > 0);

/**
 * In-progress keyframe-stepper session (adjusted box per visited keyframe, step index, and the
 * start/end frame of the range), keyed by annotationId. Module-level so it survives the popover
 * closing + re-opening on each step (moving the playhead scrolls the timeline, which closes the
 * popover) — this is what pins Start to the first keyframe and keeps a user's End edit across steps.
 */
export type KeyframeStepperSessionT = {
  annotationId: string;
  startFrame: number;
  endFrame: number;
  stepIndex: number;
  keyFrames: Map<number, BoundingBoxT>;
};
export const keyframeStepperSessionAtom = atom<KeyframeStepperSessionT | null>(null);